import { fg } from '@atlaskit/platform-feature-flags';
import type { Metric } from '@atlassian/browser-metrics/types';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	isFollowUpMessage,
	isStreamActionError,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamError,
	isStreamReasoningPart,
	isStreamResearchPayload,
	isStreamResearchPlan,
	isStreamResponse,
	type ParsedSSEMessage,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { exponentialRetry } from '@atlassian/conversation-assistant-service/common/utils/exponential-retry';
import {
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { reconnectionMetrics } from '../../common/utils/browser-metrics';
import { randomIntFromInterval } from '../../common/utils/id-generator';

import { NoFinalResponseError, StreamErrorException, StreamGeneratorError } from './error-classes';
import { clearTracesIfNotEmpty } from './send-message-action';
import { createStoreAbortController } from './store-abort-controller';
import type { ChatStoreAction, ChatStoreContainerProps } from './types';
import {
	addAnswerPart,
	addAnswerPartWithMarkdownData,
	clearStatus,
	clearStoreAssistantMessageContent,
	createStreamMarkdownProcessor,
	extractStreamMeta,
	findStoreMessageIndex,
	getAssociatedHumanMessageId,
	getLastAgentMessageID,
	isClientSideStreamingError,
	markStreamingMessagesAsComplete,
	processStreamResponseMarkdown,
	replaceAnswerData,
	updateHumanMessageId,
	type ReconnectStreamMeta,
} from './utils';

interface ReconnectStreamMessageParams {
	generator: AsyncGenerator<ParsedSSEMessage<StreamMessage>>;
	messageId: string;
	answerIndex: number;
	conversationId: string;
	traceId: string | undefined;
	isStagingArea: boolean;
	isDeepResearch?: boolean;
	getState: () => ChatStorePrivateState;
	setMessages: ChatStoreContainerProps['setMessages'];
	getMessages: ChatStoreContainerProps['getMessages'];
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	onStreamEvent: (
		event: 'error' | 'chunk',
		streamMessage?: StreamMessage,
		eventId?: string,
	) => void;
}
export const processReconnectStreamMessage = async ({
	generator,
	messageId,
	conversationId,
	isDeepResearch,
	isStagingArea,
	answerIndex,
	traceId,
	getMessages,
	setMessages,
	processStreamResearch,
	getState,
	updateCurrentConversation,
	sendAnalyticsEvent,
	onStreamEvent,
}: ReconnectStreamMessageParams): Promise<{
	hasReceivedFinalResponse: boolean;
}> => {
	const processStreamMarkdown = createStreamMarkdownProcessor();
	let latestStreamMessage: StreamMessage | undefined;
	let hasReceivedFinalResponse = false;

	try {
		for await (const streamObj of generator!) {
			const streamMessage = streamObj.data;
			latestStreamMessage = streamMessage;
			onStreamEvent('chunk', streamMessage, streamObj.id);
			/**
			 * Research payload processing
			 */
			if (
				isStreamResearchPayload(streamMessage) ||
				isStreamResponse(streamMessage) ||
				isStreamAnswerPart(streamMessage) ||
				isStreamReasoningPart(streamMessage)
			) {
				processStreamResearch({
					messageId,
					streamMessage,
					isDeepResearch: isDeepResearch ?? false,
				});
			}
			/**
			 * Research plan processing
			 */
			if (isStreamResearchPlan(streamMessage)) {
				const messages = clearStoreAssistantMessageContent(getMessages(conversationId), messageId);
				setMessages(conversationId, messages);
				processStreamResearch({
					messageId: messageId,
					streamMessage,
					isDeepResearch: isDeepResearch ?? false,
				});
			}

			/**
			 *  ANSWER_PART  processing
			 */
			if (isStreamAnswerPart(streamMessage)) {
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				let processedStreamMessage = streamMessage;
				let processedStreamResult = null;

				if (fg('rovo_enable_component_streaming')) {
					processedStreamResult = processStreamMarkdown(processedStreamMessage);
					processedStreamMessage = processedStreamResult.processedStreamMessage;
				}

				if (
					processedStreamResult &&
					processedStreamResult.markdownData &&
					fg('rovo_enable_component_streaming')
				) {
					setMessages(
						conversationId,
						addAnswerPartWithMarkdownData({
							chat: getMessages(conversationId),
							answerId: messageId,
							answerPart: processedStreamMessage,
							markdownData: processedStreamResult.markdownData,
							useIndex: answerIndex,
						}),
					);
				} else {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: messageId,
							answerPart: streamMessage,
							useIndex: answerIndex,
						}),
					);
				}
			}

			/**
			 * FINAL_RESPONSE processing
			 */
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				const humanMessageId = streamMessage?.message?.human_message?.id.toString();
				const { processedStreamMessage, markdownData } = processStreamResponseMarkdown({
					streamMessage,
					messageId,
					conversationId,
					sendAnalyticsEvent,
				});

				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: messageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
					}),
				);

				// Update the StoreHuman entry's ID from the temporary client-generated ID
				// to the real server-assigned ID. After a page reload this is a no-op because
				// the store already contains the real ID.
				if (humanMessageId) {
					const currentHumanMessageId = getAssociatedHumanMessageId(
						getMessages(conversationId),
						messageId,
					);
					if (currentHumanMessageId && currentHumanMessageId !== humanMessageId) {
						setMessages(
							conversationId,
							updateHumanMessageId({
								chat: getMessages(conversationId),
								oldId: currentHumanMessageId,
								newId: humanMessageId,
							}),
						);
					}
				}
			}

			/**
			 * Follow up processing
			 */
			if (isFollowUpMessage(streamMessage)) {
				const messages = getMessages(conversationId);
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: streamMessage,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
					},
				]);
			}

			/**
			 * Action error processing
			 */
			if (isStreamActionError(streamMessage)) {
				const actionError = new StreamErrorException(
					streamMessage.message.data?.message || 'Action execution failed',
					{
						detail: {
							message: {
								content: streamMessage?.message?.data?.message || 'Action execution failed',
								message_template: streamMessage?.message?.key,
							},
							metadata: {
								...streamMessage.metadata,
								isStagingArea,
							},
						},
						traceId,
					},
				);
				throw actionError;
			}

			if (isStreamError(streamMessage)) {
				onStreamEvent('error', streamMessage, streamObj.id);
				const { message, metadata, millisOffset, type } = streamMessage;

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId,
				});
			}
		}
	} catch (error: any) {
		if (error instanceof StreamErrorException) {
			throw error;
		}

		throw new StreamGeneratorError(
			error.message ?? 'Stream generator error',
			{
				traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			error,
		);
	}

	return { hasReceivedFinalResponse };
};

export const createReconnectionMetrics = (params: {
	isDeepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
}): {
	timeToConnectToStreamMetrics: Metric;
	timeToResumeStreamMetrics: Metric;
} => {
	const timeToConnectToStreamMetrics = reconnectionMetrics.createTimeToConnectToStreamMetrics({
		deepResearchEnabled: params.isDeepResearchEnabled,
		thinkDeeperEnabled: params.thinkDeeperEnabled,
		fastModeEnabled: params.fastModeEnabled,
	});

	const timeToResumeStreamMetrics = reconnectionMetrics.createTimeToResumeStreamMetrics({
		deepResearchEnabled: params.isDeepResearchEnabled,
		thinkDeeperEnabled: params.thinkDeeperEnabled,
		fastModeEnabled: params.fastModeEnabled,
	});

	return {
		timeToConnectToStreamMetrics,
		timeToResumeStreamMetrics,
	};
};

const MAX_RECONNECT_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;
const POST_ONLINE_DELAY_MS = 2000;
const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

/**
 * Determines if an error from the reconnection stream is retryable.
 * Only client-side/network errors are retried. Server-side errors (AUP, stream errors),
 * HTTP errors, and user aborts are not.
 */
export const isRetryableReconnectionError = (error: unknown): boolean => {
	if (error instanceof Error && error.name === 'AbortError') {
		return false;
	}

	// StreamGeneratorError wraps the original error as `cause` — check the underlying error
	if (error instanceof StreamGeneratorError) {
		return isClientSideStreamingError(error.cause ?? error);
	}

	return isClientSideStreamingError(error);
};

export type ChatStoreActionsForReconnect = {
	setConversationData: (params: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
};

export type ReconnectCompletionResult = {
	success: boolean;
	error?: Error;
	streamMeta?: ReconnectStreamMeta;
};

export type ReconnectToInProgressStreamParams = {
	conversationId: string;
	messageId: string;
	lastEventId?: string;
	answerIndex?: number;
	isDeepResearch?: boolean;
	abortCurrentRequest?: boolean;
	onComplete?: (result: ReconnectCompletionResult) => void;
	isAutoReconnect?: boolean;
};

export const reconnectToInProgressStreamProvider: (
	params: ReconnectToInProgressStreamParams & ChatStoreActionsForReconnect,
) => ChatStoreAction =
	({
		conversationId,
		messageId,
		answerIndex: _answerIndex,
		isDeepResearch,
		lastEventId,
		setConversationData,
		abortCurrentRequest,
		onComplete,
		isAutoReconnect,
	}) =>
	async (
		{ getState, dispatch },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent,
			trackAIMAUEvent,
			getMessages,
			setMessages,
			processStreamResearch,
			chatContextStoreActions,
			markIncompleteResearchesErrored,
			endReconnection,
			startReconnection,
			isReconnecting,
			getIsChatMounted,
		},
	) => {
		const currentConversation = getState().conversations[conversationId];

		if (abortCurrentRequest && currentConversation?.storeAbortController) {
			currentConversation.storeAbortController.abort();
		}

		// Skip reconnection if there is another operation in progress
		if (
			(currentConversation?.storeAbortController && !abortCurrentRequest) ||
			isReconnecting(conversationId)
		) {
			return;
		}

		startReconnection(conversationId, messageId);

		const reconnectionPerformanceMetrics = createReconnectionMetrics({
			isDeepResearchEnabled: isDeepResearch,
			thinkDeeperEnabled: false,
			fastModeEnabled: false,
		});

		let totalAttempts = 0;
		const reconnectStartedAt = new Date().toISOString();

		const metrics = {
			// Reconnection metrics
			onReconnectStarted: () => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.start();
				experienceTracker.start({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					id: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					attributes: {
						conversationId,
						messageId,
						isDeepResearch,
					},
				});
				// Only fire MAU events for page-load auto-reconnects, not send-message reconnects.
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'initiated',
						userGenerated: false,
						proactive: true,
						triggeredByAutoReconnect: true,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						timestamp: reconnectStartedAt,
					});
				}
			},
			// Was able to connect to active stream
			onReconnectEstablished: () => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.stop();
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.start();
			},
			onReconnectFailed: (error: Error) => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.cancel();
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.cancel();
				experienceTracker.fail({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					error: error,
					attributes: {
						conversationId,
						messageId,
						isDeepResearch,
						totalAttempts,
					},
				});
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'error',
						userGenerated: false,
						proactive: true,
						triggeredByAutoReconnect: true,
						messageId,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						timestamp: new Date().toISOString(),
					});
				}
			},
			onReconnectChunkReceived: () => {
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.stop();
			},
			onReconnectAborted: () => {
				experienceTracker.abort({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					reason: 'Aborted',
				});
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.cancel();
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.cancel();
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'dismissed',
						userGenerated: false,
						proactive: true,
						triggeredByAutoReconnect: true,
						messageId,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						timestamp: new Date().toISOString(),
					});
				}
			},
			onReconnectSucceeded: () => {
				experienceTracker.succeed({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					attributes: {
						totalAttempts,
						conversationId,
						messageId,
						isDeepResearch,
					},
				});
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'viewed',
						userGenerated: false,
						proactive: true,
						triggeredByAutoReconnect: true,
						messageId,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						citedProductList: streamMeta?.citedProductList ?? {},
						sourceProducts: streamMeta?.sourceProducts ?? {},
						pluginsInvoked: streamMeta?.pluginsInvoked,
						skillsInvoked: streamMeta?.skillsInvoked,
						executedStratusSkill: streamMeta?.executedStratusSkill,
						responseSources: streamMeta?.responseSources,
						usesJsm: streamMeta?.usesJsm,
						messageActions: streamMeta?.messageActions,
						conversationChannelId: conversationId,
						timestamp: new Date().toISOString(),
					});
				}
			},
		};

		const updateCurrentConversation = (data: Partial<Conversation>) => {
			dispatch(
				setConversationData({
					conversationId,
					data,
				}),
			);
		};

		const cancelMessageStream = () => {
			assistanceService.cancelMessageStream({ conversationId, messageId }).catch((err: any) => {
				logException({
					name: 'rovoCancelMessageStream',
					exception: err,
					tags: { conversationId, messageId },
				});
			});
		};

		const messages = getMessages(conversationId);
		const answerIndex = _answerIndex ?? findStoreMessageIndex(messages, messageId);
		const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();
		let traceId: string | undefined;
		let streamMeta: ReconnectStreamMeta | undefined;
		let currentLastEventId = lastEventId;
		let reconnectSucceeded = false;

		const connectAndProcessStream = async () => {
			const { generator, responseData } = await assistanceService.reconnectToMessageStream({
				conversationId,
				messageId,
				lastEventId: currentLastEventId,
				controller: abortController,
			});

			traceId = responseData?.traceId;

			metrics.onReconnectEstablished();

			const { hasReceivedFinalResponse } = await processReconnectStreamMessage({
				generator,
				traceId: responseData?.traceId,
				messageId,
				conversationId,
				answerIndex,
				isStagingArea: chatContextStoreActions.getIsStagingArea(),
				isDeepResearch,
				getState,
				setMessages,
				getMessages,
				processStreamResearch,
				sendAnalyticsEvent,
				updateCurrentConversation,
				onStreamEvent: (event, streamMessage, eventId) => {
					// Update currentLastEventId progressively as we process each event
					// so that retries can resume from the last successfully processed event
					if (eventId) {
						currentLastEventId = eventId;
					}

					if (event === 'chunk') {
						metrics.onReconnectChunkReceived();

						if (streamMessage && isStreamResponse(streamMessage)) {
							streamMeta = extractStreamMeta(streamMessage as StreamResponse);
						}
					}
					if (event === 'error') {
						if (isStreamAUPError(streamMessage)) {
							reconnectSucceeded = true;
							metrics.onReconnectSucceeded();
						}
					}
				},
			});

			if (abortController.signal.aborted) {
				throw new DOMException('Reconnect aborted', 'AbortError');
			}

			if (!hasReceivedFinalResponse) {
				throw new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{
						traceId,
					},
				);
			}
		};

		//** CONNECT TO STREAM (with retry) */
		try {
			metrics.onReconnectStarted();
			updateCurrentConversation({
				status: CHAT_STORE_STATUS.typingResponse,
				traces: [{ messageContent: 'Resuming response...', status: 'loading' }],
				storeAbortController,
			});

			await exponentialRetry({
				fn: connectAndProcessStream,
				maxRetries: MAX_RECONNECT_ATTEMPTS,
				delay: RETRY_BASE_DELAY_MS,
				postOnlineDelay: POST_ONLINE_DELAY_MS,
				errorFilter: (error) => {
					// Don't retry if the chat is unmounted — lazy reconnection will handle it when user returns
					if (!getIsChatMounted()) {
						abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
						return false;
					}
					return isRetryableReconnectionError(error);
				},
				jitter: true,
				signal: abortController.signal,
				pauseWhileOffline: true,
				onRetry: (attempt, _error) => {
					totalAttempts = attempt + 1;
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'retrying',
						actionSubject: 'rovoChatReconnection',
						attributes: {
							conversationId,
							messageId,
							isDeepResearch,
							attempt,
							maxAttempts: MAX_RECONNECT_ATTEMPTS,
							hasLastEventId: !!currentLastEventId,
						},
					});
					updateCurrentConversation({
						traces: [
							{
								messageContent: `Connection lost. Retrying... (${attempt}/${MAX_RECONNECT_ATTEMPTS})`,
								status: 'loading',
							},
						],
					});
				},
			});

			metrics.onReconnectSucceeded();
			onComplete?.({ success: true, streamMeta });
		} catch (error) {
			if (
				(error instanceof Error && error.name === 'AbortError') ||
				abortController.signal.aborted
			) {
				confirmAbort();
				updateCurrentConversation({ lastAgentMessageStatus: 'FAILED' });
				metrics.onReconnectAborted();
				// Only cancel the message stream if the abort reason is not the chat unmounted reason
				if (abortController.signal.reason !== RECONNECT_ABORT_REASON_CHAT_UNMOUNTED) {
					cancelMessageStream();
				}
				return;
			}

			if (!reconnectSucceeded) {
				metrics.onReconnectFailed(error as Error);
			}

			if (isDeepResearch) {
				markIncompleteResearchesErrored({ messageId });
			}

			onComplete?.({
				success: reconnectSucceeded,
				error: error instanceof Error ? error : new Error(String(error)),
			});
			throw error;
		} finally {
			endReconnection(conversationId);
			updateCurrentConversation(clearStatus({}));
			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
		}
	};
